
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import App from './App';
import MailInbox from './components/MailInbox';
import CreateTemplate from './components/CreateTemplate';
import DatePlan from './components/Date/DatePlan';
import TableData from './components/Table/TableData';
import InboxData from './components/Inbox/InboxData';
import Login from './components/Login/Login';
import User from './components/User/User';
import Whatsapp from './components/Whatsapp/Whatsapp';
import initialiseInterceptor, { abortPendingRequests } from './services/interceptor';
import { startActivityReporter } from './utils/idleSession';
import { startDraftGuard, userIdFromStored } from './utils/drafts';
import Admin from './components/Admin/Admin';
import AddUser from './components/AddUser/AddUser';
import TemplateTable from './components/Template/TemplateTable';
import UserTable from './components/UserManage/UserTable';
import PermissionsTable from './components/Permissions/permissionsTable';
import UserAssignBoard from './components/UserManage/UserAssignBoard';
import GroupAssignment from './components/GroupAssignment/GroupAssignment';
import GroupTable from './components/GroupAssignment/GroupTable';
import AutomationsTable from './components/Automation/AutomationsTable';
import AuthListener from './components/Login/AuthListener';
import ForceClockInGate from './components/ForceClockInGate';
import AuthenticationMsg from './common/AuthenticationMsg';
import ArchivedBoard from './components/ArchivedBoard/ArchivedBoard';
import Loader from './utils/Loading'
import ShowDraft from './components/Draft/ShowDraftList';
import BoardManage from './components/AddBoard/BoardManage';
import BoardAllocation from './components/BoardAllocation/BoardAllocation';
import ImapMailboxTable from './components/ImapAuthUser/ImapMailboxTable';
import OutlookMailboxTable from './components/OutlookAuthUser/OutlookMailboxTable';
import WebmailDetail from './components/Webmail/WebmailDetail';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from './utils/Localstorage/localStorageUtils';

// bu-admin's own top-level help sections (see HelpSectionForm's Section
// dropdown, where any string can be typed in as a new route key).
const deriveKanbanSection = (pathname) => {
    const clean = (pathname || '').replace(/^\/+/, '').split('?')[0];
    const segments = clean.split('/').filter(Boolean);
    if (segments.length > 1) {
        const last = segments[segments.length - 1];
        const looksLikeId = /^[0-9a-f-]{8,}$/i.test(last) || /^\d+$/.test(last);
        if (looksLikeId) segments.pop();
    }
    return segments.length ? `kanban/${segments.join('/')}` : 'kanban';
};

// Layout effects run before every passive effect of the new page, so only the
// previous page's in-flight GETs are aborted, never the new page's.
const AbortOnRouteChange = () => {
    const location = useLocation();
    useLayoutEffect(() => {
        abortPendingRequests();
    }, [location.pathname]);
    return null;
};

const RouteSectionReporter = () => {
    const location = useLocation();
    useEffect(() => {
        if (window.self === window.top) return;
        // path: lets the admin app keep its own address (?iframePath=) in step with this page
        window.parent.postMessage(
            {
                type: 'IFRAME_ROUTE_CHANGE',
                section: deriveKanbanSection(location.pathname),
                path: location.pathname + location.search,
            },
            '*'
        );
    }, [location.pathname, location.search]);
    return null;
};

// Whatsapp.js seeds most of its state from location.state via useState() once
// on mount, so navigating from ticket to ticket on the same /detail/:id route
// wouldn't otherwise refresh it. Keying by id forces a clean remount per ticket.
const DetailPageKeyed = ({ parentOrigin }) => {
    const { id } = useParams();
    return <Whatsapp key={id} parentOrigin={parentOrigin} />;
};

const DeepLinkResolver = ({ isLoggedIn }) => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLoggedIn) return;

        const params = new URLSearchParams(location.search);
        const redirectParam = params.get('redirect');
        const iframePathParam = params.get('iframePath');
        const encodedTarget = redirectParam || iframePathParam;
        if (!encodedTarget) return;

        let decodedTarget = encodedTarget;
        try {
            decodedTarget = decodeURIComponent(encodedTarget);
        } catch (error) {
            decodedTarget = encodedTarget;
        }

        let normalizedTarget = decodedTarget;
        try {
            const parsed = new URL(decodedTarget, window.location.origin);
            if (parsed.origin !== window.location.origin) return;
            normalizedTarget = `${parsed.pathname}${parsed.search}${parsed.hash}`;
        } catch (error) {
            normalizedTarget = decodedTarget;
        }

        if (!normalizedTarget.startsWith('/')) return;
        if (normalizedTarget.startsWith('/detail/')) {
            navigate(normalizedTarget, { replace: true });
            return;
        }

        if (normalizedTarget.startsWith('/app')) {
            navigate(normalizedTarget, { replace: true });
        }
    }, [isLoggedIn, location.search, navigate]);

    return null;
};

const AppRoute = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(localStorage.getItem('access_token') || '');
    const [isInIframe, setIsInIframe] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [parentOrigin, setParentOrigin] = useState('')
    const [hasUserData, setHasUserData] = useState(() => {
        return !!getLocalStorageItem('userData');
    });

    // Report real activity (clicks, typing) to the server so the admin app around
    // this iframe doesn't log the user out as idle while they work on the board.
    // Unsaved work: when the admin app around this iframe logs the user out by itself it
    // asks us to keep what was typed; restored after logging in again (Phase 5).
    useEffect(() => {
        startDraftGuard({ getUserId: () => userIdFromStored(getLocalStorageItem('userData')) });
    }, []);

    useEffect(() => startActivityReporter({
        baseUrl: String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, ''),
        getToken: () => localStorage.getItem('access_token'),
        getCompanyId: () => getLocalStorageItem('company-id'),
    }), []);

    useEffect(() => {
        initialiseInterceptor();
        const inIframe = window.self !== window.top;
        setIsInIframe(inIframe);
        if (inIframe) {
            const checkAuth = () => {
                const token = localStorage.getItem('access_token');
                const userData = getLocalStorageItem('userData');
                if (token && userData) {
                    setIsReady(true);
                } else {
                    setIsReady(false);
                }
            };
            // Inside BU: wait for BU's fresh login (AUTH_DATA) instead of starting with the
            // token left from the last session — that one may be dead, and its first
            // request would log the whole app out. Fall back to it if BU never answers.
            const fallback = setTimeout(() => {
                if (!window.__buParentAuthAt) checkAuth();
            }, 4000);
            const onStorage = () => checkAuth();
            window.addEventListener('storage', onStorage);
            return () => {
                clearTimeout(fallback);
                window.removeEventListener('storage', onStorage);
            };
        } else {
            const timeoutId = setTimeout(() => {
                setIsReady(true);
            }, 50);
            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, []);

    useEffect(() => {
        const messageHandler = (event) => {
            const { token, userData, parentOrigin, companyId, companySubscription, groupPermissions } = event.data;
            let updated = false;
            if (token) {
                localStorage.setItem('access_token', token);
                setIsLoggedIn(token);
                window.__buParentAuthAt = Date.now(); // BU handed over its login (see interceptor)
                updated = true;
            } if (companyId) {
                setLocalStorageItem('company-id', companyId);
                setHasUserData(true);
                updated = true;
            }
            if (userData) {
                setLocalStorageItem('userData', userData);
                setHasUserData(true);
                updated = true;
            }
            if (companySubscription) {
                setLocalStorageItem('companySubscription', companySubscription);
                if (companySubscription.companyId && !companyId) {
                    setLocalStorageItem('company-id', companySubscription.companyId);
                }
                setHasUserData(true);
                updated = true;
            }
            if (groupPermissions) {
                setLocalStorageItem('groupPermissions', groupPermissions);
                updated = true;
            }
            if (event.data.isRemoveItem === true) {
                localStorage.clear();
                removeLocalStorageItem()
                setIsLoggedIn('');
                setHasUserData(false);
            }
            if (updated && isInIframe) {
                const tokenNow = localStorage.getItem('access_token');
                const userDataNow = getLocalStorageItem('userData');
                if (tokenNow && userDataNow) {
                    setIsReady(true);
                }
            }
            if (parentOrigin) {
                setParentOrigin(parentOrigin);
            }
        };
        window.addEventListener('message', messageHandler);
        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, [isInIframe]);

    const handleAuthChange = (token) => {
        setIsLoggedIn(token);
        setHasUserData(!!getLocalStorageItem('userData'));
        if (isInIframe) {
            const tokenNow = localStorage.getItem('access_token');
            const userDataNow = getLocalStorageItem('userData');
            if (tokenNow && userDataNow) {
                setIsReady(true);
            } else {
                setIsReady(false);
            }
        }
    };

    if (!isReady) {
        return <Loader />;
    }

    return (
        <BrowserRouter>
            <DeepLinkResolver isLoggedIn={!!isLoggedIn} />
            <RouteSectionReporter />
            <AbortOnRouteChange />
            <AuthListener setIsLoggedIn={setIsLoggedIn} />
            {isLoggedIn && !isInIframe && <ForceClockInGate />}
            <Routes>

                <Route path="/app" element={<App onAuthChange={handleAuthChange} />} />
                <Route path="/kanban" element={<App onAuthChange={handleAuthChange} parentOrigin={parentOrigin} />} />
                <Route path="/authentication-msg" element={<AuthenticationMsg />} />

                {!isLoggedIn ? (
                    <>
                        <Route path="/login" element={<Login />} />
                        <Route path="*" element={<Navigate to={isInIframe ? "/app" : "/login"} />} />
                    </>
                ) : (
                    <>
                        <Route path="*" element={<Navigate to="/app" />} />
                        <Route path="/app" element={<App onAuthChange={handleAuthChange} parentOrigin={parentOrigin} />} />
                        <Route path="/kanban" element={<App onAuthChange={handleAuthChange} parentOrigin={parentOrigin} />} />
                        <Route path="/mailinbox" element={<MailInbox />} />
                        <Route path="/createtemplate" element={<CreateTemplate />} />
                        <Route path="/dateplan" element={<DatePlan />} />
                        <Route path="/tabledata" element={<TableData />} />
                        <Route path="/inbox" element={<InboxData />} />
                        <Route path="/user" element={<User />} />
                        <Route path="/detail/:id" element={<DetailPageKeyed parentOrigin={parentOrigin} />} />
                        <Route path="/webmail-detail/:id" element={<WebmailDetail />} />
                        <Route path="/admin" element={<Admin />} />
                        <Route path="/adduser" element={<AddUser />} />
                        <Route path="/template" element={<TemplateTable />} />
                        <Route path="/usermanage" element={<UserTable />} />
                        <Route path="/permissions" element={<PermissionsTable />} />
                        <Route path="/assignboard" element={<UserAssignBoard />} />
                        <Route path="/groupassignment" element={<GroupAssignment />} />
                        <Route path="/group" element={<GroupTable />} />
                        <Route path="/automations" element={<AutomationsTable />} />
                        <Route path="/draft" element={<ShowDraft />} />
                        <Route path="/archivedboard" element={<ArchivedBoard />} />
                        <Route path="/boardmanage" element={<BoardManage />} />
                        <Route path="/boardallocation" element={<BoardAllocation />} />
                        <Route path="/webmail-accounts" element={<ImapMailboxTable />} />
                        <Route path="/outlook-accounts" element={<OutlookMailboxTable />} />

                    </>
                )}
            </Routes>
        </BrowserRouter>
    );
};

export default AppRoute;
